import React, { useEffect, useState } from 'react';
import './SwagModal.scss';

// 同源路由（经 Cloudflare Worker 提供），避免 workers.dev 在国内被阻断
const SWAG_MAP_URL = '/api/swag_map.json';
const SWAG_SUBMIT_URL = '/api/swag/submit';
const SWAG_VERIFY_URL = '/api/swag/verify';
const SWAG_CHECK_URL = '/api/swag/check_session';
const VERIFY_BOT_URL = 'https://t.me/L2IVerifyBot';

const TOKEN_KEY = 'l2i_swag_token';
const USER_KEY = 'l2i_swag_user';

const SWAG_OPTIONS = ['11月7日 周六 珠海', '11月29日 周日 上海', '12月6日 周日 广州', '其他'];

// 中文赠送名单（可点击填报）
const ZH_LIST = [
  '@4everyang', '@AlexRowe', '@Anderseas', '@AzureCandy', '@BGDajiu', '@C7June',
  '@csdlbx', '@Dacongmingya', '@Daydayuppp', '@Dengdage', '@fffancy', '@genthz',
  '@hezc1928', '@irisierend', '@Kingmos', '@Leexiaoyu', '@lzsvictor', '@Michael0071',
  '@NanoApe', '@Nrabbit', '@PJ10086', '@PurpleRedRiver', '@RichardL1', '@SailingY',
  '@Snowrainbow', '@SSSunlight', '@TabrisJ', '@Xyinkl',
];

// 💡 填报流程文案：仅中文界面提供（验证、场次、代领）
const F = {
  formTitle: '领取方式填报',
  sessionLabel: '选择场次',
  ledQuestion: '是否由他人代领？',
  ledYes: '由他人代领',
  ledNo: '自行领取',
  ledByPh: '代领人 ID（如 @Xyinkl）',
  errLedBy: '选择"由他人代领"时需填写代领人 ID',
  customPh: '请输入具体内容…',
  submit: '提交',
  submitting: '提交中…',
  submitted: '✅ 已提交，以最新填报为准',
  confirmTip: (id: string, e: SwagEntry) => `@${id} 已填报（当前: ${e.choice === '其他' ? `其他 ${e.custom || ''}` : e.choice}${e.led_by ? `，代领 @${e.led_by}` : ''}）\n重新填报将以最新提交为准，是否继续？`,
  goOn: '继续填报',
  cancel: '取消',
  errNetwork: '提交失败，请稍后重试',
  errCustom: '选择"其他"时需填写具体内容',
  needVerify: '身份验证',
  verifyDesc: '填报前需验证你的 Telegram 账号（用于登记核验）：\n1. 点击下方按钮打开验证 bot\n2. 发送任意消息，bot 会回复 6 位验证码\n3. 在此输入验证码完成验证',
  verifyGoBot: '打开 @L2IVerifyBot',
  codePh: '输入 6 位验证码',
  verifyBtn: '验证',
  verifying: '验证中…',
  verifiedAs: (u: string) => `✔ 已验证: @${u}`,
  errCode: '验证码错误或已过期',
  errReVerify: '验证已过期，请重新验证',
};

type SwagEntry = { choice: string; custom?: string; led_by?: string; ts: string };
type Lang = 'zh' | 'en' | 'jp';

interface SwagModalProps {
  open: boolean;
  lang: Lang;
  setLang: (l: Lang) => void;
  onClose: () => void;
}

export default function SwagModal({ open, lang, setLang, onClose }: SwagModalProps) {
  const [swagMap, setSwagMap] = useState<Record<string, SwagEntry>>({});
  // 当前填报的 ID（不带 @）
  const [activeId, setActiveId] = useState<string | null>(null);
  // 已填报 ID 需先确认是否重新填报
  const [confirming, setConfirming] = useState(false);
  const [choice, setChoice] = useState('');
  const [custom, setCustom] = useState('');
  const [ledChoice, setLedChoice] = useState<'' | 'led' | 'solo'>('');
  const [ledBy, setLedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [justSubmitted, setJustSubmitted] = useState(false);

  // 💡 TG 验证状态
  const [vToken, setVToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [vUser, setVUser] = useState<string | null>(() => localStorage.getItem(USER_KEY));
  const [codeInput, setCodeInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSwagMap({});
    setActiveId(null);
    setConfirming(false);
    setChoice('');
    setCustom('');
    setLedChoice('');
    setLedBy('');
    setError('');
    setJustSubmitted(false);
    setCodeInput('');
    setVerifyError('');
    fetch(SWAG_MAP_URL, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : {}))
      .then(setSwagMap)
      .catch(() => {});
    // 校验本地会话 token 是否仍有效
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetch(SWAG_CHECK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
        .then(r => r.json())
        .then(d => {
          if (!d.ok) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setVToken(null);
            setVUser(null);
          } else {
            setVUser(d.username);
            localStorage.setItem(USER_KEY, d.username);
          }
        })
        .catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const texts = {
    zh: {
      title: 'SWAG // 纪念周边派发',
      body1: '🎁 【OP.LINK2INFINITY】\n纪念周边派发通知\n各位 Agent，时间过得真快！\n最长Link周边终于制作完成，即将开始派发啦！\n\n📦 【4in1 纪念套装】内容包含：\n 纪念棒球 x1\n 纪念光栅卡 x1\n 纪念魔术贴 x1\n 专属纸盒 x1\n\n📋 中文赠送名单（每人一套）\n（点击你的 ID 填报领取方式）',
      body2: '6881 GiftBox\n在未来三个月\n将会通过各种渠道\n寄送、带给各位！\n\n再次感谢🙏',
    },
    en: {
      title: 'SWAG // DISTRIBUTION',
      body1: '🎁 [OP.LINK2INFINITY] SwagBox Distribution Notice\nHello Agents, time really flies!\n\nThe 6881 SwagBox is finally ready and will be making its way to you soon!\n\n📦 The [4-in-1 SwagBox] includes:\n Commemorative Baseball x1\n Lenticular Card x1\n Velcro Patch x1\n Custom Display Box x1\n\n🚚 About Shipping & Logistics:\n 🇺🇸 US: Please contact @Unclebeldin to coordinate.\n 🇨🇦 Canada: Please contact @Ba1r0g to coordinate.\n\n📋 Recipient List (Everyone on the list receives one set)\n🇺🇸 US\n@IceNavigator\n@lostngone\n@Sonicrx\n@Unclebeldin\n\n🇨🇦 Canada\n@Ba1r0g\n@Masterfod',
      body2: 'That single link meant so much, and I truly appreciate everyone\'s support.\nThank you, all! 🙏',
    },
    jp: {
      title: 'SWAG // 配布案内',
      body1: '🎁 【OP.LINK2INFINITY】記念スワッグ配布のお知らせ\n\nエージェントの皆さん、こんにちは！時が経つのは本当に早いですね。\n\n記念スワッグがついに完成し、順次皆さんにお届けしていきます！\n本当に、感謝すべきは私のほうです。\n\n📦 【4in1 記念セット】内容：\n 記念ベースボール x1\n レンチキュラーカード x1\n ベルクロパッチ x1\n 専用ボックス x1\n\n🚚 配送・受け取りについて：\n 🇯🇵 日本： @chiakeshi にご連絡ください。日本へは直接郵送が可能です。また、もし中国に来られるご友人がいらっしゃれば、代理受け取りも可能です。\n\n📋 対象者リスト（リストの皆様全員に1セット贈呈します）\n🇯🇵 日本\n@chiakeshi\n@CrystaTech\n@jejejejely\n@komecco\n@MikanZuki\n@yantensh',
      body2: 'あの1本のリンクには多くの意味が込められていました。\nサポートいただき本当にありがとうございました。\nまたいつか、一緒に作戦（Op）を決行できる日を楽しみにしています！',
    },
  };

  const t = texts[lang];

  const handleIdClick = (rawId: string) => {
    const id = rawId.replace(/^@/, '');
    setError('');
    setChoice('');
    setCustom('');
    setLedChoice('');
    setLedBy('');
    setJustSubmitted(false);
    if (swagMap[id]) {
      setConfirming(true);
    } else {
      setConfirming(false);
    }
    setActiveId(id);
  };

  const handleVerify = async () => {
    if (verifying) return;
    if (!/^\d{6}$/.test(codeInput.trim())) {
      setVerifyError(F.errCode);
      return;
    }
    setVerifying(true);
    setVerifyError('');
    try {
      const res = await fetch(SWAG_VERIFY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: codeInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error('bad');
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, data.username);
      setVToken(data.token);
      setVUser(data.username);
      setCodeInput('');
    } catch {
      setVerifyError(F.errCode);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!activeId || submitting) return;
    if (choice === '其他' && !custom.trim()) {
      setError(F.errCustom);
      return;
    }
    if (ledChoice === 'led' && !ledBy.trim().replace(/^@/, '')) {
      setError(F.errLedBy);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(SWAG_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: activeId,
          choice,
          custom: custom.trim(),
          led_by: ledChoice === 'led' ? ledBy.trim() : '',
          token: vToken,
        }),
      });
      const data = await res.json();
      if (res.status === 403 || data.error === 'verify required') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setVToken(null);
        setVUser(null);
        setError(F.errReVerify);
        return;
      }
      if (!res.ok || !data.ok) throw new Error('bad');
      setSwagMap(prev => ({ ...prev, [activeId]: { choice, custom: custom.trim(), led_by: ledChoice === 'led' ? ledBy.trim().replace(/^@/, '') : '', ts: new Date().toISOString() } }));
      setChoice('');
      setCustom('');
      setLedChoice('');
      setLedBy('');
      setConfirming(false);
      setActiveId(null);
      setJustSubmitted(true);
    } catch {
      setError(F.errNetwork);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="swag-modal">
      <div className="swag-backdrop" onClick={onClose} />
      <div className="swag-panel">
        <div className="swag-topbar">
          <span className="swag-title">{t.title}</span>
          <span className="swag-lang">
            <button className={lang === 'zh' ? 'active' : ''} onClick={() => setLang('zh')}>中</button><span>|</span>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button><span>|</span>
            <button className={lang === 'jp' ? 'active' : ''} onClick={() => setLang('jp')}>JP</button>
          </span>
          <button className="swag-close" onClick={onClose}>✕</button>
        </div>

        <img className="swag-video" src="/swag-intro.gif" alt="6881 SwagBox Intro" />

        <div className="swag-text">{t.body1}</div>

        {lang === 'zh' && (
          <>
            <div className="swag-id-grid">
              {ZH_LIST.map(rawId => {
                const id = rawId.replace(/^@/, '');
                const filled = !!swagMap[id];
                return (
                  <button
                    key={rawId}
                    className={`swag-id${filled ? ' filled' : ''}${activeId === id ? ' active' : ''}`}
                    onClick={() => handleIdClick(rawId)}
                  >
                    {rawId}{filled && <span className="swag-check">✔</span>}
                  </button>
                );
              })}
            </div>

            {/* 已验证状态提示（未选中 ID 时显示） */}
            {vToken && vUser && !activeId && (
              <div className="swag-verified-chip">{F.verifiedAs(vUser)}</div>
            )}

            {/* 未验证：点击 ID 后先走验证流程 */}
            {activeId && !vToken && (
              <div className="swag-form">
                <div className="swag-form-title">{`@${activeId} · ${F.needVerify}`}</div>
                <div className="swag-verify-desc">{F.verifyDesc}</div>
                <a
                  className="swag-btn swag-verify-link"
                  href={VERIFY_BOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {F.verifyGoBot}
                </a>
                <input
                  className="swag-input"
                  value={codeInput}
                  placeholder={F.codePh}
                  maxLength={6}
                  inputMode="numeric"
                  onChange={e => setCodeInput(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
                />
                {verifyError && <div className="swag-error">{verifyError}</div>}
                <button className="swag-btn primary" onClick={handleVerify} disabled={verifying}>
                  {verifying ? F.verifying : F.verifyBtn}
                </button>
              </div>
            )}

            {/* 已验证：已填报 ID 需先确认是否覆盖 */}
            {activeId && vToken && confirming && (
              <div className="swag-form">
                <div className="swag-verified-chip">{F.verifiedAs(vUser as string)}</div>
                <div className="swag-form-title">{F.confirmTip(activeId, swagMap[activeId])}</div>
                <div className="swag-confirm-btns">
                  <button className="swag-btn primary" onClick={() => setConfirming(false)}>{F.goOn}</button>
                  <button className="swag-btn" onClick={() => { setActiveId(null); setConfirming(false); }}>{F.cancel}</button>
                </div>
              </div>
            )}

            {/* 已验证：填报表单 */}
            {activeId && vToken && !confirming && (
              <div className="swag-form">
                <div className="swag-verified-chip">{F.verifiedAs(vUser as string)}</div>
                <div className="swag-form-title">{`@${activeId} · ${F.formTitle}`}</div>

                <div className="swag-field-label">{F.sessionLabel}</div>
                <div className="swag-options">
                  {SWAG_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      className={`swag-opt${choice === opt ? ' selected' : ''}`}
                      onClick={() => { setChoice(opt); setError(''); }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {choice === '其他' && (
                  <input
                    className="swag-input"
                    value={custom}
                    placeholder={F.customPh}
                    maxLength={100}
                    onChange={e => setCustom(e.target.value)}
                  />
                )}

                <div className="swag-field-label">{F.ledQuestion}</div>
                <div className="swag-options">
                  <button
                    className={`swag-opt${ledChoice === 'led' ? ' selected' : ''}`}
                    onClick={() => { setLedChoice('led'); setError(''); }}
                  >
                    {F.ledYes}
                  </button>
                  <button
                    className={`swag-opt${ledChoice === 'solo' ? ' selected' : ''}`}
                    onClick={() => { setLedChoice('solo'); setLedBy(''); setError(''); }}
                  >
                    {F.ledNo}
                  </button>
                </div>
                {ledChoice === 'led' && (
                  <input
                    className="swag-input"
                    value={ledBy}
                    placeholder={F.ledByPh}
                    maxLength={33}
                    onChange={e => setLedBy(e.target.value)}
                  />
                )}

                {error && <div className="swag-error">{error}</div>}
                <button className="swag-btn primary" onClick={handleSubmit} disabled={!choice || !ledChoice || submitting}>
                  {submitting ? F.submitting : F.submit}
                </button>
              </div>
            )}

            {activeId === null && justSubmitted && (
              <div className="swag-submitted-hint">{F.submitted}</div>
            )}
          </>
        )}

        <div className="swag-text">{t.body2}</div>
      </div>
    </div>
  );
}
